import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function InviteModal({ open, onClose, workspaceId, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/invites`, { email, role });
      setInviteLink(data.invite_link);
      setEmailSent(data.email_sent);
      toast.success(data.email_sent ? "Invite email sent!" : "Invite created — email delivery failed, share the link manually");
      onInvited && onInvited();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setEmail("");
    setRole("member");
    setInviteLink(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && reset()}>
      <DialogContent data-testid="invite-team-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Invite Teammate</DialogTitle>
        </DialogHeader>
        {!inviteLink ? (
          <form onSubmit={submit} className="space-y-4">
            <Input
              type="email"
              required
              placeholder="teammate@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-input"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="invite-role-select"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="member">Member — upload & view results</option>
              <option value="admin">Admin — full workspace control</option>
            </select>
            <Button type="submit" disabled={loading} data-testid="send-invite-btn" className="w-full">
              <Send className="h-4 w-4" /> {loading ? "Sending..." : "Send Invite"}
            </Button>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {emailSent ? "Invite email sent. You can also share this link directly:" : "Email delivery failed — share this link with your teammate directly:"}
            </p>
            <div className="flex w-full min-w-0 items-center gap-2">
              <input
                readOnly
                value={inviteLink}
                onFocus={(e) => e.target.select()}
                data-testid="invite-link-text"
                className="min-w-0 flex-1 rounded-lg border border-input bg-muted px-3 py-2 font-mono text-xs text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(inviteLink);
                  toast.success("Invite link copied");
                }}
                data-testid="copy-invite-link-btn"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={reset} className="w-full" data-testid="close-invite-modal-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
